
// frontend/src/pages/LeaguePage.js
// ELITE VERSION 2.0: Centered Hero, Mini-Nav, Advanced SEO
import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { GridBackground } from '@/components/GridBackground';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { getLeagueConfig } from '@/components/Sports/SportsConfig';
import { ScoreboardGrid } from '@/components/Sports/ScoreboardGrid';
import { NewsGrid } from '@/components/Sports/NewsGrid';
import { IPTVFunnel } from '@/components/Sports/IPTVFunnel';
import { StandingsTable } from '@/components/Sports/StandingsTable';
import { UpcomingFixtures } from '@/components/Sports/UpcomingFixtures';
import {
    ArrowLeft, Trophy, Tv, Radio,
    Globe, Zap, Flag, Activity, ChevronRight, HelpCircle
} from 'lucide-react';

// ==========================================
// 1. ELITE MINI-NAV COMPONENT
// ==========================================
const SCRIPT_LEAGUES = [
    { id: 'premier-league', label: 'EPL', icon: Globe, color: 'text-purple-400' },
    { id: 'nba', label: 'NBA', icon: Zap, color: 'text-orange-400' },
    { id: 'nfl', label: 'NFL', icon: Tv, color: 'text-blue-400' },
    { id: 'champions-league', label: 'UCL', icon: Trophy, color: 'text-yellow-400' },
    { id: 'f1', label: 'F1', icon: Flag, color: 'text-red-500' },
];

const EliteMiniNav = ({ currentSlug }) => {
    const [hovered, setHovered] = useState(null);
    const navigate = useNavigate();

    return (
        <div className="flex justify-center mb-8">
            <div className="relative flex items-center gap-2 p-2 bg-black/40 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl">
                {SCRIPT_LEAGUES.map((league) => {
                    const isActive = currentSlug === league.id;
                    const Icon = league.icon;

                    return (
                        <div key={league.id} className="relative">
                            <motion.button
                                onClick={() => navigate(`/sports/${getLeagueCategory(league.id)}/${league.id}`)}
                                onHoverStart={() => setHovered(league.id)}
                                onHoverEnd={() => setHovered(null)}
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                className={`
                                    relative z-10 w-10 h-10 rounded-full flex items-center justify-center 
                                    transition-all duration-300
                                    ${isActive
                                        ? 'bg-white/15 text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                                        : 'text-muted-foreground hover:text-white hover:bg-white/5'}
                                `}
                            >
                                <Icon className={`w-5 h-5 ${isActive ? league.color : ''}`} />
                            </motion.button>

                            {/* Expanding Tooltip */}
                            <AnimatePresence>
                                {hovered === league.id && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                        animate={{ opacity: 1, y: -45, scale: 1 }}
                                        exit={{ opacity: 0, y: 5, scale: 0.8 }}
                                        className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 pointer-events-none z-20"
                                    >
                                        <div className="px-3 py-1.5 bg-black/80 backdrop-blur-md border border-white/10 rounded-lg whitespace-nowrap">
                                            <span className={`text-xs font-bold ${league.color}`}>{league.label}</span>
                                        </div>
                                        {/* Arrow */}
                                        <div className="w-2 h-2 bg-black/80 border-r border-b border-white/10 absolute left-1/2 -translate-x-1/2 -bottom-1 rotate-45" />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Helper to determine category path (could be in config, but updated here for quick linking)
const getLeagueCategory = (id) => {
    if (['nba'].includes(id)) return 'basketball';
    if (['nfl'].includes(id)) return 'football';
    if (['f1'].includes(id)) return 'racing';
    return 'soccer';
};

// ==========================================
// 2. MAIN PAGE COMPONENT
// ==========================================
export const LeaguePage = () => {
    const { sportSlug, leagueSlug } = useParams();
    const config = getLeagueConfig(leagueSlug);

    // Validate sportSlug matches config to prevent duplicate content
    if (config && config.sport !== sportSlug) {
        return <Navigate to={`/sports/${config.sport}/${leagueSlug}`} replace />;
    }

    useEffect(() => {
        if (config?.name) {

            window.scrollTo(0, 0);
        }
    }, [leagueSlug, config]);

    if (!config) {
        return <Navigate to="/sports" replace />;
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "SportsOrganization",
        "name": config.name,
        "url": `https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`,
        "logo": "https://xtreamgate.com/logo-sports.png", // Ensure this exists or use a generic one
        "description": `Live scores, news, and streaming guide for ${config.name}.`,
        "sameAs": [
            "https://twitter.com/xtreamgate",
            "https://facebook.com/xtreamgate"
        ]
    };

    // Breadcrumb Schema
    const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://xtreamgate.com"
        }, {
            "@type": "ListItem",
            "position": 2,
            "name": "Sports",
            "item": "https://xtreamgate.com/sports"
        }, {
            "@type": "ListItem",
            "position": 3,
            "name": config.name
        }]
    };

    // FAQ Schema
    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
            "@type": "Question",
            "name": `How can I watch ${config.name} live without cable?`,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": `You can watch ${config.name} live streams using high-quality IPTV services compatible with Firestick, Android TV, and Apple TV. XtreamGate recommends verified providers with 4K support and anti-freeze technology.`
            }
        }, {
            "@type": "Question",
            "name": `Where can I find real-time ${config.name} scores?`,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": `XtreamGate Sports Hub provides real-time live scores, results, and fixtures for ${config.name}, updated every minute.`
            }
        }, {
            "@type": "Question",
            "name": `Is there a free trial for ${config.name} streaming?`,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, many premium IPTV providers listed on XtreamGate offer 24-36 hour free trials so you can test the stream stability for major games before subscribing."
            }
        }]
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-background">
            {/* ELITE SEO STANDARDS */}
            <Helmet>
                <title>Watch {config.name} Live | Scores, Stream & News 2025</title>
                <meta name="title" content={`Watch ${config.name} Live | Scores, Stream & News 2025`} />
                <meta name="description" content={`The ultimate guide to ${config.name} live streaming. Get real-time scores, latest stats, and learn how to watch ${config.name} games in 4K without cable.`} />
                <meta name="keywords" content={`${config.name} live stream, watch ${config.name} free, ${config.name} scores, ${config.name} news, ${config.name} iptv, sports streaming`} />

                {/* Canonical */}
                <link rel="canonical" href={`https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`} />
                <meta property="og:title" content={`Watch ${config.name} Live | XtreamGate Hub`} />
                <meta property="og:description" content={`Real-time scores and streaming guide for ${config.name}. Don't miss a game.`} />
                <meta property="og:image" content="https://xtreamgate.com/og-sports.jpg" />
                <meta property="og:site_name" content="XtreamGate" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${config.name} Live Coverage`} />
                <meta name="twitter:description" content={`Live scores, news and streaming info for ${config.name}.`} />
                <meta name="twitter:image" content="https://xtreamgate.com/og-sports.jpg" />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify([jsonLd, breadcrumbLd, faqLd])}
                </script>
            </Helmet>

            <MainNavigation />
            <GridBackground />




            {/* BREADCRUMBS (Visual) */}
            <nav className="relative z-10 py-4 px-4">
                <div className="max-w-7xl mx-auto">
                    <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li>/</li>
                        <li><Link to="/sports" className="hover:text-primary transition-colors">Sports Hub</Link></li>
                        <li>/</li>
                        <li><span className="text-primary font-medium">{config.name}</span></li>
                    </ol>
                </div>
            </nav>

            {/* CENTERED HERO SECTION with MINI-NAV */}
            <section className="relative z-10 pt-32 pb-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        {/* 0. Back Link (Centered) */}
                        <div className="flex justify-center mb-6">
                            <Link
                                to="/sports"
                                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors group"
                            >
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                Back to Hub
                            </Link>
                        </div>

                        {/* 1. Mini Nav */}
                        <EliteMiniNav currentSlug={leagueSlug} />

                        {/* 2. Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                            <Activity className="w-3 h-3 text-primary animate-pulse" />
                            <span className="text-xs font-bold text-primary tracking-widest uppercase">
                                Real-time Data Hub
                            </span>
                        </div>

                        {/* 3. Title */}
                        <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tighter leading-[0.9]">
                            <span className={`text-transparent bg-clip-text bg-gradient-to-br ${config.color} drop-shadow-2xl`}>
                                {config.name}
                            </span>
                        </h1>

                        {/* 4. Description */}
                        <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-light">
                            The ultimate destination for {config.name}. <br className="hidden md:block" />
                            <span className="text-white font-medium">Live scores</span>, <span className="text-white font-medium">breaking news</span>, and <span className="text-white font-medium">4K streams</span>.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* MAIN CONTENT GRID */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 pb-24 space-y-20">

                {/* Scoreboard */}
                <ScoreboardGrid sport={config.sport} league={config.id} />

                {/* Standings & Fixtures Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <StandingsTable sport={config.sport} league={config.id} leagueSlug={leagueSlug} limit={10} />
                    <UpcomingFixtures sport={config.sport} league={config.id} limit={8} />
                </div>

                {/* IPTV CTA */}
                <IPTVFunnel sportName={config.name} />

                {/* News */}
                <div className="space-y-8">
                    <div className="flex items-center justify-center gap-3 mb-8">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                        <span className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
                            Latest Headlines
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
                    </div>
                    <NewsGrid category={config.newsCategory} />
                </div>

                {/* How to Watch Section */}
                <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
                    <div className="p-6 border-b border-white/5">
                        <div className="flex items-center gap-3">
                            <Tv className="w-6 h-6 text-primary" />
                            <h2 className="text-xl font-bold text-white">How to Watch {config.name}</h2>
                        </div>
                        <p className="text-muted-foreground mt-2">
                            Stream every {config.name} match in 4K without cable. Here's your complete guide.
                        </p>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Link
                            to="/blog/best-iptv-for-sports"
                            className="group p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 transition-all"
                        >
                            <div className="flex items-center gap-2 text-primary mb-2">
                                <Zap className="w-4 h-4" />
                                <span className="text-sm font-bold">Best IPTV for Sports</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Top-rated services for NFL, NBA, Premier League
                            </p>
                        </Link>
                        <Link
                            to="/devices/firestick"
                            className="group p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 transition-all"
                        >
                            <div className="flex items-center gap-2 text-primary mb-2">
                                <Tv className="w-4 h-4" />
                                <span className="text-sm font-bold">Firestick Setup</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                5-minute guide to streaming on Amazon Fire TV
                            </p>
                        </Link>
                        <a
                            href="https://xtreamcube.shop/freetrialiptv/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-4 rounded-xl bg-gradient-to-br from-primary/20 to-blue-600/20 border border-primary/30 hover:border-primary/50 transition-all"
                        >
                            <div className="flex items-center gap-2 text-white mb-2">
                                <Trophy className="w-4 h-4" />
                                <span className="text-sm font-bold">Free Trial</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Test {config.name} streaming free for 24 hours
                            </p>
                        </a>
                    </div>
                    {/* Share Section */}
                    <div className="px-6 py-4 bg-white/5 border-t border-white/5 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Share this page:</span>
                        <div className="flex items-center gap-3">
                            <a
                                href={`https://twitter.com/intent/tweet?text=Live ${config.name} Scores&url=https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary/20 transition-colors"
                                title="Share on Twitter"
                            >
                                <span className="text-sm">𝕏</span>
                            </a>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-blue-600/30 transition-colors"
                                title="Share on Facebook"
                            >
                                <span className="text-sm">f</span>
                            </a>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(`https://xtreamgate.com/sports/${config.sport}/${leagueSlug}`);
                                    alert('Link copied!');
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary/20 transition-colors"
                                title="Copy Link"
                            >
                                <span className="text-sm">🔗</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                            <HelpCircle className="w-4 h-4 text-primary" />
                            <span className="text-xs font-bold text-white uppercase">Common Questions</span>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-4">Watching {config.name} Guide</h2>
                        <p className="text-muted-foreground">Everything you need to know about streaming {config.name} live.</p>
                    </div>

                    <div className="space-y-4">
                        {[
                            { q: `How can I watch ${config.name} live without cable?`, a: `You can watch ${config.name} live streams using high-quality IPTV services compatible with Firestick, Android TV, and Apple TV. We recommend services with 4K support and anti-freeze technology.` },
                            { q: `Where can I find real-time ${config.name} scores?`, a: `XtreamGate Sports Hub provides real-time live scores, results, and fixtures for ${config.name}, updated every minute.` },
                            { q: `What internet speed do I need for 4K streaming?`, a: `For buffer-free 4K streaming of ${config.name} matches, we recommend a stable internet connection of at least 25 Mbps.` }
                        ].map((item, i) => (
                            <div key={i} className="group rounded-2xl border border-white/10 bg-white/5 hover:border-primary/30 transition-all overflow-hidden">
                                <details className="group">
                                    <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                                        <h3 className="text-lg font-medium text-white group-hover:text-primary transition-colors pr-8">
                                            {item.q}
                                        </h3>
                                        <ChevronRight className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
                                    </summary>
                                    <div className="px-6 pb-6 pt-0 text-muted-foreground leading-relaxed">
                                        {item.a}
                                    </div>
                                </details>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};
